/**
 * React Query hooks for finding/vulnerability data.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as findingsApi from '@/api/findings';
import type { FindingFilters, FindingStatusUpdateRequest } from '@/types/finding';

/** Fetch findings with optional filters. */
export function useFindings(filters?: FindingFilters) {
  return useQuery({
    queryKey: ['findings', filters],
    queryFn: () => findingsApi.getFindings(filters),
  });
}

/** Fetch aggregated finding summary statistics. */
export function useFindingSummary() {
  return useQuery({
    queryKey: ['findings', 'summary'],
    queryFn: () => findingsApi.getFindingSummary(),
  });
}

/** Fetch a single finding by ID. */
export function useFinding(findingId: string) {
  return useQuery({
    queryKey: ['findings', findingId],
    queryFn: () => findingsApi.getFinding(findingId),
    enabled: !!findingId,
  });
}

/** Mutation to update a finding's status. */
export function useUpdateFindingStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ findingId, payload }: { findingId: string; payload: FindingStatusUpdateRequest }) =>
      findingsApi.updateFindingStatus(findingId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['findings'] });
    },
  });
}

/** Mutation to delete a single finding. */
export function useDeleteFinding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) => findingsApi.deleteFinding(findingId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['findings'] }),
  });
}

/** Mutation to delete multiple findings at once. */
export function useBulkDeleteFindings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => findingsApi.bulkDeleteFindings(ids),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['findings'] }),
  });
}

/** Mutation to trigger a targeted rescan for a finding. */
export function useTriggerRescan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (findingId: string) => findingsApi.triggerRescan(findingId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['findings'] });
      queryClient.invalidateQueries({ queryKey: ['scans'] });
    },
  });
}
